import { Plugin, Widget } from 'ckeditor5'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { toWidget } from 'ckeditor5'
import { WordSearch } from '@minicoursegenerator/word-search'

// Transform config - API data is already in correct format, just pass through
const transformConfigForWordSearch = (config) => {
  if (!config) return config
  return { ...config }
}

const DEFAULT_WORD_SEARCH_JSON = {
  config: {
    language: 'en',
    bgColor: '#ffffff',
    primaryColor: '#0ea5e9',
    accentColor: '#10b981',
    gridBackgroundColor: '#f0f9ff',
    textColor: '#1e293b',
    cardBackgroundColor: '#ffffff',
    wordItemBackgroundColor: '#f8fafc'
  },
  words: [
    'HELLO',
    'WORLD',
    'SEARCH',
    'PUZZLE'
  ]
}

export default class WordSearchPlugin extends Plugin {
  static get pluginName() {
    return 'WordSearchPlugin'
  }

  static get requires() {
    return [Widget]
  }

  constructor(editor) {
    super(editor)
    this.mountedRoots = new Map()
    this._updateTimeout = null
    this._lastConfigStr = null
  }

  init() {
    const editor = this.editor

    editor.model.schema.register('wordSearch', {
      isObject: true,
      allowWhere: '$block',
      allowIn: 'column',
      allowAttributes: [
        'id',
        'data',
        'gameConfig',
      ],
    })

    this._defineConverters()
    this._setupAttributeListener()

    editor.ui.componentFactory.add('insertWordSearch', locale => {
      const button = document.createElement('button')
      button.className = 'ck-button ck-button_with-text'
      button.style.display = 'flex'
      button.style.alignItems = 'center'
      button.style.gap = '4px'
      button.style.padding = '8px 12px'

      const icon = document.createElement('span')
      icon.textContent = '🔍'
      icon.style.fontSize = '16px'

      const text = document.createTextNode('Word Search')

      button.appendChild(icon)
      button.appendChild(text)

      button.onclick = () => {
        editor.execute('insertWordSearch')
      }

      return {
        element: button,
        render: () => { },
        destroy: () => { },
      }
    })

    // Command to insert via button (uses default JSON)
    editor.commands.add('insertWordSearch', {
      execute: () => {
        editor.model.change(writer => {
          const wordSearchLanguage = editor.wordSearchLanguage || 'en'
          const newConfig = JSON.parse(JSON.stringify(DEFAULT_WORD_SEARCH_JSON))
          if (newConfig.config) {
            newConfig.config.language = wordSearchLanguage
          }

          // Get bgColor from config or colors.background
          const bgColor = newConfig?.config?.bgColor || newConfig?.colors?.background || '#ffffff'

          const simpleBoxWrapper = writer.createElement('simpleBoxWrapper', {
            backgroundColor: bgColor,
          })
          const simpleBox = writer.createElement('simpleBox')
          const column = writer.createElement('column')
          const wordSearch = writer.createElement('wordSearch', {
            gameConfig: JSON.stringify(newConfig),
          })

          writer.append(simpleBox, simpleBoxWrapper)
          writer.append(column, simpleBox)
          writer.append(wordSearch, column)

          editor.model.insertContent(simpleBoxWrapper)
          writer.setSelection(wordSearch, 'on')
        })
      },
    })
  }

  _setupAttributeListener() {
    const editor = this.editor

    editor.model.document.on('change:data', () => {
      const changes = editor.model.document.differ.getChanges()

      for (const change of changes) {
        if (change.type === 'attribute' && change.attributeKey === 'gameConfig') {
          const modelElement = change.range.start.nodeAfter
          if (modelElement && modelElement.name === 'wordSearch') {
            const newGameConfig = modelElement.getAttribute('gameConfig')
            this._updateWordSearchComponent(modelElement, newGameConfig)
          }
        }
      }
    })
  }

  _updateWordSearchComponent(modelElement, gameConfig) {
    if (gameConfig === this._lastConfigStr) {
      return
    }

    this._lastConfigStr = gameConfig

    if (this._updateTimeout) {
      clearTimeout(this._updateTimeout)
    }

    this._updateTimeout = setTimeout(() => {
      const editor = this.editor

      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (!viewElement) return

      const domConverter = editor.editing.view.domConverter
      const containerDom = domConverter.mapViewToDom(viewElement)
      if (!containerDom) return

      const placeholder = containerDom.querySelector('.word-search-react-placeholder')
      if (!placeholder) return

      const existingRoot = this.mountedRoots.get(placeholder)
      if (existingRoot) {
        try {
          const parsedConfig = gameConfig ? JSON.parse(gameConfig) : DEFAULT_WORD_SEARCH_JSON
          const transformedConfig = transformConfigForWordSearch(parsedConfig)
          const configLanguage = transformedConfig?.config?.language || transformedConfig?.language || editor.wordSearchLanguage || 'en'

          const props = {
            data: transformedConfig,
            language: configLanguage,
            onComplete: () => console.log('🔍 Word Search Completed!'),
            onFail: () => console.log('🔍 Word Search Failed!'),
            onChange: state => console.log('🔍 Word Search State:', state),
          }

          console.log('🔍 Word Search re-rendering with new config:', props)
          existingRoot.render(React.createElement(WordSearch, props))
        } catch (error) {
          console.error('Error re-rendering Word Search component:', error)
        }
      }
    }, 500)
  }

  _defineConverters() {
    const editor = this.editor
    const conversion = editor.conversion

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        attributes: {
          'data-type': 'word-search',
        },
      },
      model: (viewElement, { writer }) => {
        const id = viewElement.getAttribute('id')
        const data = viewElement.getAttribute('data') || ''
        const gameConfig = viewElement.getAttribute('data-game-config') || ''

        const attrs = {
          data,
          gameConfig,
        }
        if (id) attrs.id = id

        return writer.createElement('wordSearch', attrs)
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'wordSearch',
      view: (modelElement, { writer }) => {
        const id = modelElement.getAttribute('id')
        const data = modelElement.getAttribute('data') || ''
        const gameConfig = modelElement.getAttribute('gameConfig') || ''

        const attrs = {
          'data-type': 'word-search',
          data: data,
          'data-game-config': gameConfig,
        }

        if (id !== undefined && id !== null) {
          attrs.id = id
        }

        return writer.createEmptyElement('div', attrs)
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'wordSearch',
      view: (modelElement, { writer }) => {
        const gameConfig = modelElement.getAttribute('gameConfig') || ''

        const containerAttrs = {
          class: 'word-search-widget-container',
          'data-type': 'word-search',
          'data-game-config': gameConfig,
        }

        const container = writer.createContainerElement('div', containerAttrs)

        const placeholder = writer.createRawElement(
          'div',
          {
            class: 'word-search-react-placeholder',
          },
          domElement => {
            const stopPropagation = e => e.stopPropagation()
            const events = ['mousedown', 'click', 'dblclick', 'pointerdown', 'pointermove', 'pointerup', 'keydown', 'keyup', 'input']
            events.forEach(evt => domElement.addEventListener(evt, stopPropagation))
            domElement.addEventListener('focus', stopPropagation, true)
            domElement.addEventListener('blur', stopPropagation, true)

            console.log('🔍 Word Search rendering started')
            try {
              const parsedConfig = gameConfig ? JSON.parse(gameConfig) : DEFAULT_WORD_SEARCH_JSON
              const transformedConfig = transformConfigForWordSearch(parsedConfig)
              const configLanguage = transformedConfig?.config?.language || transformedConfig?.language || editor.wordSearchLanguage || 'en'

              const props = {
                data: transformedConfig,
                language: configLanguage,
                onComplete: () => console.log('🔍 Word Search Completed!'),
                onFail: () => console.log('🔍 Word Search Failed!'),
                onChange: state => console.log('🔍 Word Search State:', state),
              }

              console.log('🔍 Word Search Props (Initial):', props)

              const root = createRoot(domElement)
              root.render(React.createElement(WordSearch, props))

              editor.plugins
                .get('WordSearchPlugin')
                .mountedRoots.set(domElement, root)
            } catch (error) {
              console.error('Error rendering Word Search component:', error)
              domElement.innerHTML = `
                <div style="padding: 20px; background: #f5f5f5; border-radius: 8px; text-align: center;">
                  <span style="font-size: 24px;">🔍</span>
                  <p>Word Search</p>
                  <small>Component not available</small>
                </div>
              `
            }
          },
        )

        writer.insert(writer.createPositionAt(container, 0), placeholder)

        return toWidget(container, writer, {
          label: 'Word Search',
          hasSelectionHandle: true,
        })
      },
    })
  }
}
